// src/pages/AdminMarketplacePage.jsx
// Admin curation UI for the marketplace — feature/unfeature listings,
// set featured_order, view all listings across all creators.

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AdminMarketplacePage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null);
  const [filter, setFilter]     = useState('all'); // all | featured | unfeatured

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    load();
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    const { supabase } = await import('../lib/supabase');
    const { data } = await supabase
      .from('marketplace_listings')
      .select('*, profiles!marketplace_listings_user_id_fkey(username, display_name)')
      .eq('status', 'published')
      .order('featured_order', { ascending: true, nullsFirst: false });
    setListings(data || []);
    setLoading(false);
  };

  const toggleFeatured = async (listing) => {
    setSaving(listing.id);
    const { supabase } = await import('../lib/supabase');
    const newFeatured = !listing.featured;
    const newOrder = newFeatured
      ? (Math.max(...listings.filter(l => l.featured).map(l => l.featured_order || 0), 0) + 1)
      : null;
    const { error } = await supabase
      .from('marketplace_listings')
      .update({ featured: newFeatured, featured_order: newOrder })
      .eq('id', listing.id);
    if (!error) {
      setListings(prev => prev.map(l => l.id === listing.id
        ? { ...l, featured: newFeatured, featured_order: newOrder }
        : l
      ));
    }
    setSaving(null);
  };

  const updateOrder = async (listing, delta) => {
    const featured = listings
      .filter(l => l.featured)
      .sort((a, b) => (a.featured_order || 99) - (b.featured_order || 99));
    const idx = featured.findIndex(l => l.id === listing.id);
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= featured.length) return;

    const a = featured[idx];
    const b = featured[swapIdx];
    const { supabase } = await import('../lib/supabase');
    await Promise.all([
      supabase.from('marketplace_listings').update({ featured_order: b.featured_order }).eq('id', a.id),
      supabase.from('marketplace_listings').update({ featured_order: a.featured_order }).eq('id', b.id),
    ]);
    setListings(prev => prev.map(l => {
      if (l.id === a.id) return { ...l, featured_order: b.featured_order };
      if (l.id === b.id) return { ...l, featured_order: a.featured_order };
      return l;
    }));
  };

  const displayed = listings.filter(l =>
    filter === 'featured' ? l.featured :
    filter === 'unfeatured' ? !l.featured : true
  );

  if (!isAdmin) return null;

  return (
    <div style={s.page}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={s.title}>Marketplace Curation</h1>
          <p style={s.sub}>{listings.filter(l => l.featured).length} featured · {listings.length} total listings</p>
        </div>
        <button onClick={load} style={s.btnSecondary}>↻ Refresh</button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['all', 'All'], ['featured', '✦ Featured'], ['unfeatured', 'Not featured']].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} style={{
            padding: '6px 14px', borderRadius: 20, border: '0.5px solid var(--border2)',
            fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer',
            background: filter === id ? 'var(--accent-dim)' : 'var(--surface)',
            color: filter === id ? 'var(--accent)' : 'var(--muted)',
            fontWeight: filter === id ? 600 : 400,
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--dim)' }}>Loading…</div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--dim)' }}>No listings found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {displayed
            .sort((a, b) => {
              if (a.featured && !b.featured) return -1;
              if (!a.featured && b.featured) return 1;
              return (a.featured_order || 99) - (b.featured_order || 99);
            })
            .map(listing => (
              <div key={listing.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', background: 'var(--surface)',
                border: `0.5px solid ${listing.featured ? 'rgba(245,158,11,0.35)' : 'var(--border)'}`,
                borderRadius: 12,
                background: listing.featured ? 'rgba(245,158,11,0.04)' : 'var(--surface)',
              }}>
                {/* Type badge */}
                <span style={{ fontSize: 18, flexShrink: 0 }}>
                  {listing.item_type === 'playbook' ? '📦' : '⚡'}
                </span>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {listing.title}
                    {listing.featured && <span style={{ marginLeft: 8, fontSize: 10, color: '#f59e0b' }}>✦ #{listing.featured_order}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--dim)', display: 'flex', gap: 10 }}>
                    <span>@{listing.profiles?.username || 'unknown'}</span>
                    <span>⬇ {listing.install_count || 0}</span>
                    {listing.rating_count > 0 && <span>⭐ {Number(listing.avg_rating).toFixed(1)}</span>}
                    <span style={{ color: 'var(--dim)' }}>{listing.item_type}</span>
                  </div>
                </div>

                {/* Order controls — only for featured */}
                {listing.featured && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => updateOrder(listing, -1)} style={s.orderBtn} title="Move up">↑</button>
                    <button onClick={() => updateOrder(listing, 1)}  style={s.orderBtn} title="Move down">↓</button>
                  </div>
                )}

                {/* Feature toggle */}
                <button
                  onClick={() => toggleFeatured(listing)}
                  disabled={saving === listing.id}
                  style={{
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500, flexShrink: 0,
                    background: listing.featured ? 'rgba(245,158,11,0.15)' : 'var(--surface2)',
                    border: `0.5px solid ${listing.featured ? 'rgba(245,158,11,0.4)' : 'var(--border2)'}`,
                    color: listing.featured ? '#f59e0b' : 'var(--muted)',
                    opacity: saving === listing.id ? 0.5 : 1,
                  }}>
                  {saving === listing.id ? '…' : listing.featured ? '✦ Unfeature' : 'Feature'}
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

const s = {
  page: {
    background: 'var(--bg)', padding: 'clamp(14px, 3vw, 26px)',
    fontFamily: 'var(--font)', minHeight: '100%',
  },
  title: { fontSize: 'clamp(18px,4vw,24px)', fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '-0.02em' },
  sub:   { fontSize: 13, color: 'var(--muted)', margin: '3px 0 0' },
  btnSecondary: {
    background: 'none', color: 'var(--muted)', border: '0.5px solid var(--border2)',
    borderRadius: 8, padding: '7px 14px', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer',
  },
  orderBtn: {
    width: 28, height: 28, background: 'var(--surface2)', border: '0.5px solid var(--border2)',
    borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 14, color: 'var(--muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
